/**
 * UserTable - Table component for displaying users.
 *
 * Coordinates the display of user data with loading, empty, and data states.
 */

import { Card, CardHeader, CardTitle, CardContent } from '@lean-agility/design-system/react';
import { UserTableContent, UserTableLoading, UserTableEmpty } from '../molecules';
import type { UserResponse } from '@/lib/api';

export interface UserTableProps {
  /**
   * List of users to display.
   */
  users: UserResponse[];
  /**
   * Whether data is loading.
   */
  isLoading?: boolean;
  /**
   * Async callback when edit button is clicked.
   */
  onEdit: (user: UserResponse) => Promise<void>;
  /**
   * Async callback when delete button is clicked.
   */
  onDelete: (user: UserResponse) => Promise<void>;
  /**
   * Function to format date strings.
   */
  formatDate: (dateString: string | null | undefined) => string;
}

export function UserTable({ users, isLoading = false, onEdit, onDelete, formatDate }: UserTableProps) {
  if (isLoading) {
    return <UserTableLoading title={'Users'} />;
  }

  if (!users || users.length === 0) {
    return <UserTableEmpty title={'Users'} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{'Users'}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <UserTableContent users={users} onEdit={onEdit} onDelete={onDelete} formatDate={formatDate} />
      </CardContent>
    </Card>
  );
}
